import { useLocation, useSearchParams } from 'react-router'

import {
  PageContextEntity,
  pageContextFor as matchPageContext,
  type PageContextRoute,
  type PageContextValue,
} from '../AgentChat/pageContext'

export {
  PageContextEntity,
  type PageContextRoute,
  type PageContextValue,
  toPageContextPayload,
} from '../AgentChat/pageContext'

/**
 * Every internal page and the record it hands the agent. Order IS precedence —
 * the first match wins, so a static route (`claims/new`) must precede the
 * `:param` route that would otherwise swallow it (`claims/:claimId`).
 */
export const PAGE_CONTEXT_ROUTES: PageContextRoute[] = [
  { pattern: '/insure/internal/customers', page: 'customers', label: 'Customers' },
  {
    pattern: '/insure/internal/customers/:id',
    page: 'customer_detail',
    label: 'Customer',
    entityType: PageContextEntity.ORGANISATION,
    entityParam: 'id',
  },
  {
    // The customer page names its tab in the path — every tab is the same record.
    pattern: '/insure/internal/customers/:id/*',
    page: 'customer_detail',
    label: 'Customer',
    entityType: PageContextEntity.ORGANISATION,
    entityParam: 'id',
  },
  { pattern: '/insure/internal/sales-leads', page: 'sales_leads', label: 'Sales leads' },
  {
    pattern: '/insure/internal/sales-leads/pipeline',
    page: 'sales_pipeline',
    label: 'Sales pipeline',
    entityType: PageContextEntity.SALES_LEAD,
    entityQueryParam: 'lead',
  },
  {
    pattern: '/insure/internal/sales-leads/:id',
    page: 'sales_lead_detail',
    label: 'Sales lead',
    entityType: PageContextEntity.SALES_LEAD,
    entityParam: 'id',
  },
  { pattern: '/insure/internal/applications', page: 'applications', label: 'Applications' },
  {
    pattern: '/insure/internal/applications/:id',
    page: 'application_detail',
    label: 'Application',
    entityType: PageContextEntity.INSURANCE_APPLICATION,
    entityParam: 'id',
  },
  { pattern: '/insure/internal/requotes', page: 'requotes', label: 'Requotes' },
  { pattern: '/insure/internal/claims', page: 'claims', label: 'Claims' },
  { pattern: '/insure/internal/claims/new', page: 'claim_new', label: 'New claim' },
  {
    pattern: '/insure/internal/claims/:claimId',
    page: 'claim_detail',
    label: 'Claim',
    entityType: PageContextEntity.CLAIM,
    entityParam: 'claimId',
  },
  { pattern: '/insure/internal/cab', page: 'cab', label: 'CAB lookup' },
  {
    pattern: '/insure/internal/cab/:dotNumber',
    page: 'cab_detail',
    label: 'CAB report',
    entityType: PageContextEntity.MOTOR_CARRIER_DOT,
    entityParam: 'dotNumber',
  },
  { pattern: '/insure/internal/vin', page: 'vin', label: 'VIN lookup' },
  { pattern: '/insure/internal/vin/:vin', page: 'vin_detail', label: 'VIN lookup' },
  {
    pattern: '/insure/internal/sales/insurance-estimator',
    page: 'insurance_estimator',
    label: 'Insurance estimator',
  },
  {
    pattern: '/insure/internal/sales/insurance-estimator/:dotNumber',
    page: 'insurance_estimator_detail',
    label: 'Insurance estimator',
    entityType: PageContextEntity.MOTOR_CARRIER_DOT,
    entityParam: 'dotNumber',
  },
  { pattern: '/insure/internal/coi-generator', page: 'coi_generator', label: 'COI generator' },
  { pattern: '/insure/internal/coi-generator/:id', page: 'coi_generator_detail', label: 'COI generator run' },
  { pattern: '/insure/internal/coi-requests', page: 'coi_requests', label: 'COI requests' },
  { pattern: '/insure/internal/work-queue', page: 'work_queue', label: 'Work queue' },
  { pattern: '/insure/internal/reminders', page: 'reminders', label: 'Reminders' },
  { pattern: '/insure/internal/sequences', page: 'sequences', label: 'Sequences' },
  { pattern: '/insure/internal/sequences/:id', page: 'sequence_detail', label: 'Sequence' },
  { pattern: '/insure/internal/sequence-templates', page: 'sequence_templates', label: 'Sequence templates' },
  { pattern: '/insure/internal/emails', page: 'emails', label: 'Emails' },
  { pattern: '/insure/internal/email-reporting', page: 'email_reporting', label: 'Email reporting' },
  { pattern: '/insure/internal/sms', page: 'sms', label: 'SMS' },
  { pattern: '/insure/internal/calls', page: 'calls', label: 'Calls' },
  { pattern: '/insure/internal/quotes-analytics', page: 'quotes_analytics', label: 'Quotes analytics' },
  { pattern: '/insure/internal/carriers', page: 'carriers', label: 'Insurance carriers' },
  {
    pattern: '/insure/internal/pending-equipment-changes',
    page: 'pending_equipment_changes',
    label: 'Pending equipment changes',
  },
  { pattern: '/insure/internal/staff-users', page: 'staff_users', label: 'Staff users' },
  { pattern: '/insure/internal/staff-users/:id', page: 'staff_users', label: 'Staff user' },
  { pattern: '/insure/internal/agent-tools', page: 'agent_tools', label: 'Agent tools' },
  { pattern: '/insure/internal/agent-chat', page: 'agent_chat', label: 'Internal agent chat' },
  { pattern: '/insure/internal/agent-chat/:id', page: 'agent_chat', label: 'Internal agent chat' },
  { pattern: '/insure/internal/qa', page: 'qa', label: 'QA' },
]

/** What the internal agent should be told about the page staff are on. */
export function pageContextFor(pathname: string, searchParams: URLSearchParams): PageContextValue | null {
  return matchPageContext(pathname, searchParams, PAGE_CONTEXT_ROUTES)
}

/**
 * Re-derived on every navigation — the panel stays open while staff move
 * around, which is the whole reason page context rides on the message rather
 * than being pinned to the conversation.
 */
export function usePageContext(): PageContextValue | null {
  const { pathname } = useLocation()
  const [searchParams] = useSearchParams()
  return pageContextFor(pathname, searchParams)
}
